import { useEffect, useState } from "react";
import API from "../../../services/api";
import "./hostelFees.css";

export default function HostelFees() {
  const school = JSON.parse(localStorage.getItem("schoolAdmin"));

  const [fees, setFees] = useState([]);
  const [loading, setLoading] = useState(false);

  // =========================
  // FILTER STATES
  // =========================
  const [month, setMonth] = useState("");
  const [studentSearch, setStudentSearch] = useState("");
  const [selectedHostel, setSelectedHostel] = useState("");
  const [selectedRoom, setSelectedRoom] = useState("");

  // =========================
  // LOAD FEES
  // =========================
  const loadFees = async () => {
    try {
      setLoading(true);
      const res = await API.get(`/school-admin/hostel/fees/`, {
        headers: { "X-SCHOOL-ID": school.school.id },
      });
      setFees(res.data.fees || []);
    } catch (e) {
      alert("Failed to load hostel fees");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadFees();
  }, []);

  // =========================
  // UPDATE STATUS
  // =========================
  const updateStatus = async (feeId, status) => {
    await API.post(
      `/school-admin/hostel/fees/${feeId}/status/`,
      { status },
      { headers: { "X-SCHOOL-ID": school.school.id } }
    );
    loadFees();
  };

  // =========================
  // DELETE FEE
  // =========================
  const deleteFee = async (feeId) => {
    if (!window.confirm("Delete this hostel fee? This cannot be undone.")) {
      return;
    }

    await API.delete(`/school-admin/hostel/fees/${feeId}/`, {
      headers: { "X-SCHOOL-ID": school.school.id },
    });

    loadFees();
  };

  // =========================
  // HOSTEL & ROOM LIST
  // =========================
  const hostels = [
    ...new Set(fees.map((f) => f.hostel_name).filter(Boolean)),
  ];

  const rooms = [
    ...new Set(
      fees
        .filter((f) => f.hostel_name === selectedHostel)
        .map((f) => f.room_number)
        .filter(Boolean)
    ),
  ];

  // =========================
  // FILTER LOGIC
  // =========================
  const filteredFees = fees.filter((f) => {
    const matchMonth = month ? f.month === month : true;
    const matchStudent = studentSearch
      ? f.student_name?.toLowerCase().includes(studentSearch.toLowerCase())
      : true;
    const matchHostel = selectedHostel
      ? f.hostel_name === selectedHostel
      : true;
    const matchRoom = selectedRoom ? f.room_number === selectedRoom : true;

    return matchMonth && matchStudent && matchHostel && matchRoom;
  });

  // =========================
  // GROUPING (HOSTEL → ROOM)
  // =========================
  const groupedFees = filteredFees.reduce((acc, fee) => {
    const hostel = fee.hostel_name || "Unknown Hostel";
    const room = fee.room_number || "Unknown Room";

    if (!acc[hostel]) acc[hostel] = {};
    if (!acc[hostel][room]) acc[hostel][room] = [];

    acc[hostel][room].push(fee);
    return acc;
  }, {});

  return (
    <div className="hostel-fees-page">
      {/* HEADER */}
      <div className="fees-header">
        <div>
          <h2>🏨 Hostel Fees</h2>
          <p>Hostel → Room → Student fee management</p>
        </div>
      </div>

      {/* FILTER BAR */}
      <div className="fees-filters">
        <input
          type="text"
          placeholder="🔍 Student name"
          value={studentSearch}
          onChange={(e) => setStudentSearch(e.target.value)}
        />

        <select
          value={selectedHostel}
          onChange={(e) => {
            setSelectedHostel(e.target.value);
            setSelectedRoom("");
          }}
        >
          <option value="">All Hostels</option>
          {hostels.map((h) => (
            <option key={h} value={h}>{h}</option>
          ))}
        </select>

        <select
          value={selectedRoom}
          onChange={(e) => setSelectedRoom(e.target.value)}
          disabled={!selectedHostel}
        >
          <option value="">All Rooms</option>
          {rooms.map((r) => (
            <option key={r} value={r}>Room {r}</option>
          ))}
        </select>

        <select value={month} onChange={(e) => setMonth(e.target.value)}>
          <option value="">All Months</option>
          {[
            "January","February","March","April","May","June",
            "July","August","September","October","November","December"
          ].map((m) => (
            <option key={m} value={m}>{m}</option>
          ))}
        </select>
      </div>

      {loading && <p className="muted-text">Loading fees...</p>}

      {/* EMPTY STATE */}
      {!loading && Object.keys(groupedFees).length === 0 && (
        <div className="empty-state">
          <p>No hostel fee records found</p>
        </div>
      )}

      {/* HOSTEL GROUP */}
      {Object.entries(groupedFees).map(([hostelName, rooms]) => (
        <div key={hostelName} className="hostel-group">
          <h3 className="hostel-title">🏠 {hostelName}</h3>

          {/* ROOM GROUP */}
          {Object.entries(rooms).map(([roomNo, students]) => (
            <div key={roomNo} className="room-group">
              <h4 className="room-title">🛏 Room {roomNo}</h4>

              {students.map((f) => (
                <div className="fee-row" key={f.id}>
                  <div className="fee-left">
                    <strong>{f.student_name}</strong>
                    <span className="fee-month">Per Year</span>
                  </div>

                  <div className="fee-middle">
                    <span className="amount">₹{f.amount}</span>
                    <span className={`badge ${f.status}`}>
                      {f.status.toUpperCase()}
                    </span>
                  </div>

                  <div className="fee-actions">
                    <button
                      className="btn paid"
                      disabled={f.status === "paid"}
                      onClick={() => updateStatus(f.id, "paid")}
                    >
                      ✓ Paid
                    </button>

                    <button
                      className="btn pending"
                      disabled={f.status === "pending"}
                      onClick={() => updateStatus(f.id, "pending")}
                    >
                      ⏳ Pending
                    </button>

                    <button
                      className="btn delete"
                      disabled={f.status === "paid"}
                      onClick={() => deleteFee(f.id)}
                    >
                      🗑 Delete
                    </button>
                  </div>
                </div>
              ))}
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}